import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./form.scss";

export default function Form({ onFinish }) {
  const [formData, setFormData] = useState({
    name: "",
    contact_type: "email",
    contact_value: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    inputRef.current?.focus({ preventScroll: true });
  }, [formData.contact_type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;

    if (name === "contact_value") {
      if (formData.contact_type === "telegram") {
        v = value.replace(/\s+/g, "");
        if (v && v[0] !== "@") v = "@" + v;
      }
      if (formData.contact_type === "phone") {
        v = value.replace(/[^\d+\-\s]/g, "");
      }
    }

    setFormData((prev) => ({ ...prev, [name]: v }));
  };

  const handleTypeChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      contact_type: e.target.value,
      contact_value: "",
    }));
  };

  const validate = () => {
    const next = {};
    const { name, contact_type, contact_value, message } = formData;

    if (!name.trim()) next.name = "Please enter your name.";

    if (!contact_type) {
      next.contact_type = "Please choose a contact method.";
    } else if (!contact_value.trim()) {
      next.contact_value = "Please enter your contact details.";
    } else {
      if (contact_type === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(contact_value)) {
          next.contact_value = "Please enter a valid email address.";
        }
      }
      if (contact_type === "telegram") {
        const tgRegex = /^@([a-zA-Z0-9_]{5,})$/;
        if (!tgRegex.test(contact_value)) {
          next.contact_value =
            "Please enter a valid Telegram handle (e.g., @nickname).";
        }
      }
      if (contact_type === "phone") {
        const phoneRegex = /^[0-9+\-\s]{6,}$/;
        if (!phoneRegex.test(contact_value)) {
          next.contact_value = "Please enter a valid phone number.";
        }
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      const { name, contact_type, contact_value, message } = formData;

      await axios.post("https://landingapi.voicex.biz/api/voicex/pbx/order", {
        name: name.trim(),
        contact_type,
        contact_value: contact_value.trim(),
        message: message.trim(),
      });

      onFinish?.(true);
      setFormData({
        name: "",
        contact_type: "email",
        contact_value: "",
        message: "",
      });
      setErrors({});
    } catch (error) {
      onFinish?.(false);
    } finally {
      setSubmitting(false);
    }
  };

  const inputType = formData.contact_type === "phone" ? "tel" : "text";
  const autoComplete =
    formData.contact_type === "email"
      ? "email"
      : formData.contact_type === "phone"
        ? "tel"
        : "username";

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="input">
        <label>Enter your name</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          autoComplete="name"
          aria-invalid={Boolean(errors.name)}
        />
        {errors.name && <div className="error">{errors.name}</div>}
      </div>

      <div className="input">
        <label>Select how to contact you</label>
        <div
          className="radios"
          role="radiogroup"
          aria-invalid={Boolean(errors.contact_type)}
        >
          <label>
            <input
              type="radio"
              name="contact_type"
              value="email"
              checked={formData.contact_type === "email"}
              onChange={handleTypeChange}
            />
            <span>Email</span>
          </label>
          <label>
            <input
              type="radio"
              name="contact_type"
              value="telegram"
              checked={formData.contact_type === "telegram"}
              onChange={handleTypeChange}
            />
            <span>Telegram</span>
          </label>
          <label>
            <input
              type="radio"
              name="contact_type"
              value="phone"
              checked={formData.contact_type === "phone"}
              onChange={handleTypeChange}
            />
            <span>Phone</span>
          </label>
        </div>
        {errors.contact_type && (
          <div className="error">{errors.contact_type}</div>
        )}

        <input
          ref={inputRef}
          type={inputType}
          name="contact_value"
          value={formData.contact_value}
          onChange={handleChange}
          autoComplete={autoComplete}
          aria-invalid={Boolean(errors.contact_value)}
        />
        {errors.contact_value && (
          <div className="error">{errors.contact_value}</div>
        )}
      </div>

      <div className="input">
        <label>Describe what you need </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          aria-invalid={Boolean(errors.message)}
        />
        {errors.message && <div className="error">{errors.message}</div>}
      </div>

      <button type="submit" disabled={submitting}>
        {submitting ? "Sending..." : "OK"}
      </button>
    </form>
  );
}
